import { HgSupportedLanguage, Icon } from '@smg-real-estate/domus';
import { type SmgSplitSession } from '@smg-real-estate/vue-experiments';
import { type SSRContext } from 'vue/server-renderer';

export enum HttpStatusCode {
  OK = 200,
  Created = 201,
  Accepted = 202,
  NoContent = 204,
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  MethodNotAllowed = 405,
  TooManyRequests = 429,
  InternalServerError = 500,
}

export enum PlatformName {
  Homegate = 'hg',
  Immoscout24 = 'is24',
}

export interface AppSSRContext extends SSRContext {
  url: string;
  smgSplitSession: SmgSplitSession;
  shouldRenderPreloadLinks?: boolean;
}

export enum LocalStorageKey {
  FAVOURITE_LISTINGS = 'hg-favourites.favouriteListings',
}

export enum ValuationFlow {
  PropertyType,
  OfferType,
  PostalCode,
  StreetAndNumber,
  PropertyDetails,
  Renovation,
  RenovationYear,
  PropertyOwnership,
  WhenToSell,
  ContactDetails,
  PhoneNumber,
  AgentRecommendations,
  CodeInput,
}

export enum AnchorLinks {
  PriceDevelopment = 'price-development',
  HowItWorks = 'how-it-works',
  LocalContent = 'local-content',
  PropertyValuation = 'property-valuation',
  SellWithAgent = 'sell-with-agent',
  MarketAtlas = 'market-atlas',
  FAQ = 'faq',
  Articles = 'articles',
  LocationCarousel = 'location-carousel',
}

export enum PropertyType {
  House = 'house',
  Apartment = 'apartment',
}

export enum MapGeoLevel {
  Country = 'country',
  Canton = 'canton',
  Municipality = 'municipality',
  Town = 'ziptown',
}

export enum Pages {
  PropLanding = 'myProperties.landingPage',
  PDP = 'propertyDetails',
  CreatePropertyFromInsertionOnDemand = 'createPropertyFromInsertionOnDemand',
  PropertyEdit = 'propertyEdit',
  PropertyOverview = 'propertyOverview',
  ListingRedirect = 'listingRedirect',
  PropertyValuationForm = 'PropertyValuationForm',
  Login = 'login',
  LoginCallback = 'LoginCallback',
}

export enum PvPages {
  LP = 'landing',
  LLP = 'localLanding',
  Funnel = 'valuationFunnel',
  IV24Funnel = 'iv24ValuationFunnel',
  IV24Result = 'iv24Result',
  IV24Callback = 'iv24Callback',
  Result = 'result',
}

export enum PageSource {
  MarketAtlas = 'marketAtlas',
}

// Order: from A-Z
export enum LocalLandingLocations {
  Aarau = 'aargau',
  Basel = 'basel-city',
  Bern = 'bern',
  Luzern = 'luzern',
  StGallen = 'st-gallen',
  Zug = 'zug',
  Zurich = 'zurich',
}

export const routeLocationToGeoIdMapping: Record<LocalLandingLocations, string> = {
  [LocalLandingLocations.Aarau]: 'geo-canton-aargau',
  [LocalLandingLocations.Basel]: 'geo-canton-basel-stadt',
  [LocalLandingLocations.Bern]: 'geo-canton-bern',
  [LocalLandingLocations.Luzern]: 'geo-canton-luzern',
  [LocalLandingLocations.StGallen]: 'geo-canton-st-gallen',
  [LocalLandingLocations.Zug]: 'geo-canton-zug',
  [LocalLandingLocations.Zurich]: 'geo-canton-zuerich',
};

export const geoIdToRouteLocationMapping: Record<string, LocalLandingLocations> = {
  'geo-municipality-aarau': LocalLandingLocations.Aarau,
  'geo-municipality-basel': LocalLandingLocations.Basel,
  'geo-municipality-bern': LocalLandingLocations.Bern,
  'geo-municipality-luzern': LocalLandingLocations.Luzern,
  'geo-municipality-st-gallen': LocalLandingLocations.StGallen,
  'geo-municipality-zug': LocalLandingLocations.Zug,
  'geo-municipality-zuerich': LocalLandingLocations.Zurich,

  'geo-canton-aargau': LocalLandingLocations.Aarau,
  'geo-canton-basel-stadt': LocalLandingLocations.Basel,
  'geo-canton-bern': LocalLandingLocations.Bern,
  'geo-canton-luzern': LocalLandingLocations.Luzern,
  'geo-canton-st-gallen': LocalLandingLocations.StGallen,
  'geo-canton-zug': LocalLandingLocations.Zug,
  'geo-canton-zuerich': LocalLandingLocations.Zurich,
};

export interface PriceTrendsRangeSummary {
  range: number;
  year: string;
  evolution: string;
  evolutionType: 'increase' | 'decrease';
}

export interface FeatureFlagsResponse {
  smgSplitSession: SmgSplitSession;
}

// Same decision as MA - uses the municipality data from IAZI, but shows the city word on page instead
export const cityLocale: { [key in HgSupportedLanguage]: string } = {
  [HgSupportedLanguage.EN]: 'city',
  [HgSupportedLanguage.DE]: 'ort',
  [HgSupportedLanguage.FR]: 'lieu',
  [HgSupportedLanguage.IT]: 'luogo',
};

export const municipalityLocale: { [key in HgSupportedLanguage]: string } = {
  [HgSupportedLanguage.EN]: 'municipality',
  [HgSupportedLanguage.DE]: 'gemeinde',
  [HgSupportedLanguage.FR]: 'municipalite',
  [HgSupportedLanguage.IT]: 'comune',
};

export enum PropertyTypeValue {
  Apartment = 6,
  House = 5,
}

export enum KpiTypeValue {
  Price = 1,
  PricePerSquareMeter = 2,
  Rent = 3,
  RentPerSquareMeter = 4,
}

// matches domus breakpoints
export const BREAKPOINTS = {
  domus_xs: 320,
  domus_sm: 375,
  domus_md: 768,
  domus_lg: 1024,
  domus_xl: 1200,

  funnel_container_max_width: 480, // matches funnel-container-max-width from variables.scss
};

export enum ContactModalFrom {
  TopBanner = 'top_banner_cta',
  RecallIcon = 'recall_icon',
  AgentSell = 'agent_sell_cta',
  PropertyDetails = 'property_details_cta',
  NextStepsTeaser = 'next_steps_teaser_cta',
  Dashboard = 'dashboard_cta',
}

export enum SellingTypes {
  DIY = 'private',
  AGENT = 'agent',
}

export enum QuestionLimit {
  LP = 6,
  LLP = 3,
}

export enum ContactModalType {
  AGENCY_RECOMMENDATION = 'agency_recommendation',
  CALL_ME_BACK = 'call_me_back',
}

export enum PreferredTimeValue {
  Morning = 'from_8_to_12',
  Noon = 'from_12_to_13',
  Afternoon = 'from_13_to_17',
  Evening = 'from_17_to_18',
}

export enum PriceDevelopmentBadgeType {
  UP,
  AVERAGE,
  DOWN,
}

export enum ToastType {
  RemoveSuccess = 'RemoveSuccess',
  RemoveFailure = 'RemoveFailure',
  AddSuccess = 'AddSuccess',
  SubmitSuccess = 'SubmitSuccess',
  AddFailure = 'AddFailure',
  EditSuccess = 'EditSuccess',
  TooManyRequests = 'TooManyRequests',
  ServerFailure = 'ServerFailure',
  PrefilledData = 'PrefilledData',
  PropertyTypeChangedApartment = 'PropertyTypeChangedApartment',
  OtpSuccess = 'OtpSuccess',
  OtpLimitReached = 'OtpLimitReached',
  LocationSuccess = 'LocationSuccess',
  AgentSelectionRequired = 'AgentSelectionRequired',
  None = 'None',
}

export const NAVIGATION_KEYS = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
export const VALUATION_PROGRESSED_TOTAL_STEPS = Object.keys(ValuationFlow).length / 2;
export const BUILD_YEAR_THRESHOLD = 2000;
export const MIN_BUILD_YEAR = 1000;
export const MAX_LIVING_SPACE = 9999;
export const MAX_BUILD_YEAR = new Date().getFullYear() + 2;
export const MIN_BUILD_YEAR_CORRECT_VALUATION = 1900;
export const AXIOS_HEADERS = {
  E2E_TEST_HEADER: 'X-E2e-Test',
  API_KEY: 'X-Api-Key',
  REQUEST_SOURCE: 'X-Request-Source',
};
export const MAX_RENOVATION_YEAR = new Date().getFullYear();
export const MAX_TICK_AMOUNT = 5;
export const TEXT_PLACEHOLDER = '–';
export const STATIC_ASSETS_PRELOAD_PREFIX = 'preload-';

export const FLAGSET_PROPERTY_VALUATION = 'valuation';
export const FLAGSET_HOW_PROPERTIES = 'how_overview';

export enum LoginAction {
  AddProperty = 'Add property',
  CreatePropertyFromValuationFunnel = 'Create property from valuation funnel',
}

export interface RedirectAppState {
  action?: LoginAction;
  payload: any;
  locale: HgSupportedLanguage;
}

export const termsAndConditionsLinks = {
  hg: {
    en: 'https://www.homegate.ch/c/en/about-us/legal-issues/gbc',
    de: 'https://www.homegate.ch/c/de/ueber-uns/rechtliches/agb',
    fr: 'https://www.homegate.ch/c/fr/tout-sur-nous/informations-juridiques/cg',
    it: 'https://www.homegate.ch/c/it/chi-siamo/informazioni-giuridiche/cg',
  },
  is24: {
    en: 'https://www.immoscout24.ch/c/en/about-us/gtc',
    de: 'https://www.immoscout24.ch/c/de/ueber-uns/agb',
    fr: 'https://www.immoscout24.ch/c/fr/tout-sur-nous/conditions-generales',
    it: 'https://www.immoscout24.ch/c/it/chi-siamo/cgc',
  },
};

export const privacyLinks = {
  en: 'https://privacy.swissmarketplace.group/',
  de: 'https://privacy.swissmarketplace.group/de/',
  fr: 'https://privacy.swissmarketplace.group/fr/',
  it: 'https://privacy.swissmarketplace.group/it/',
};

export interface MunicipalityItem {
  icon: Icon;
  title: string;
  value: string;
}
